import React, { useState } from 'react';
import dayjs from 'dayjs';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CheckIcon from '@mui/icons-material/Check';
import { getDatesForWeek, getHoursByDate, getIsPerDiemByDate } from '../../helpers';
import { getHoursAndMinutes, getNumberWithDelimeter } from '../../../../../utils/helpers';
import AddGradientIcon from '../../../../common/Icons/AddGradientIcon';
import EditTimesheetModal from '../EditTimesheetModal/EditTimesheetModal';
import MainButton from '../../../../common/buttons/MainButton';
import TimesheetItem from '../EditTimesheetModal/components/TimesheetItem';
import BagageIcon from '../../../../common/Icons/BagageIcon';
import NotificationModal from '../../../../common/ModalWindows/NotificationModal';
import { EditPerDiemModal } from '../../components/modals/modals';
import BaseSwipeableDrawer from '../../../../common/BaseSwipeableDrawer/BaseSwipeableDrawer';
import classes from './TimesheetsCardList.module.scss';
import colors from '../../../../../scss/colors';
import { timesheetsStatuses } from '../../../../../utils/constants';
import EditMonthlyTimesheetDrawer from '../EditMonthlyTimesheetDrawer/EditMonthlyTimesheetDrawer';
import MonthCalendar from '../../../../common/MonthCalendar/MonthCalendar';
import { rateEnum, submissionEnum } from '../../../../../utils/enums';

const PerDiemCard = ({ days = 0, clickHandler = () => {} }) => {
  const { t } = useTranslation();

  return (
    <div role="presentation" className={classes.card} onClick={clickHandler}>
      <div className={classes.doubleText}>
        <p className={classes.perDiemLabel}>{t('dashboard.timesheet.perdiem')}</p>
      </div>
      <div className={classes.hoursWrapper}>
        <p className={classes.perDiemDays}>
          {days} {t('common.general.day')}
          {days !== 1 ? 's' : ''}
        </p>
      </div>
    </div>
  );
};

const PerDiemInfoItem = ({ day, value }) => {
  return (
    <div className={classes.infoContainer}>
      <p className={classes.dayInfo}>{day}</p>
      {value ? (
        <CheckIcon sx={{ fontSize: '16px', color: colors.PRIMARY_BLUE }} />
      ) : (
        <CheckIcon sx={{ fontSize: '16px', color: colors.WHITE }} />
      )}
    </div>
  );
};

export default function TimesheetsCardList(props) {
  const {
    data,
    perdiems,
    massiveChangePerDiem,
    startDateOfPeriod,
    addHandler,
    mobileChangeHandler,
    allowedWorkTypes,
    rateType = rateEnum.HOURLY,
    deleteLineHandler,
    submissionType,
    currentMonth,
    editable,
    status,
    author
  } = props;

  const { t } = useTranslation();

  const isApprover = useSelector((state) => state.accessToParts.tAndEApprover);

  const [openTimesheet, setOpenTimesheet] = useState(null);
  const [openPerDiems, setOpenPerDiems] = useState(null);

  const addHandlerMobile = () => {
    addHandler();
    const timesheetVal = data[data.length - 1];
    setOpenTimesheet({
      timesheet: timesheetVal,
      startDateOfPeriod
    });
  };

  const savePDHandler = (values) => {
    massiveChangePerDiem(values);
    setOpenPerDiems(null);
  };

  const closePDHandler = () => {
    setOpenPerDiems(null);
  };

  const deletePDHandler = (values) => {
    massiveChangePerDiem(values.map((value) => ({ ...value, IsPerDiem: false })));
    setOpenPerDiems(null);
  };

  const getTotalHours = (timesheet) => {
    if (submissionType === submissionEnum.MONTHLY)
      return timesheet.TimesheetHoursByDates.reduce((acc, ts) => acc + ts.TotalHrs, 0)
        .toFixed(2)
        .toString();

    const totalHoursArray = [
      getHoursByDate(timesheet.TimesheetHoursByDates, dayjs(startDateOfPeriod)),
      getHoursByDate(timesheet.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(1, 'day')),
      getHoursByDate(timesheet.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(2, 'day')),
      getHoursByDate(timesheet.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(3, 'day')),
      getHoursByDate(timesheet.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(4, 'day')),
      getHoursByDate(timesheet.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(5, 'day')),
      getHoursByDate(timesheet.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(6, 'day'))
    ];

    return parseFloat(totalHoursArray.reduce((accumulator, hours) => accumulator + Number(hours), 0))
      .toFixed(2)
      .toString();
  };

  const getTotalPerDiems = (passedPerdiems) => {
    if (submissionType === submissionEnum.MONTHLY)
      return passedPerdiems.reduce((acc, pd) => (pd.IsPerDiem ? acc + 1 : acc), 0);

    const totalPerDiemsArray = [
      getIsPerDiemByDate(passedPerdiems, dayjs(startDateOfPeriod)),
      getIsPerDiemByDate(passedPerdiems, dayjs(startDateOfPeriod).add(1, 'day')),
      getIsPerDiemByDate(passedPerdiems, dayjs(startDateOfPeriod).add(2, 'day')),
      getIsPerDiemByDate(passedPerdiems, dayjs(startDateOfPeriod).add(3, 'day')),
      getIsPerDiemByDate(passedPerdiems, dayjs(startDateOfPeriod).add(4, 'day')),
      getIsPerDiemByDate(passedPerdiems, dayjs(startDateOfPeriod).add(5, 'day')),
      getIsPerDiemByDate(passedPerdiems, dayjs(startDateOfPeriod).add(6, 'day'))
    ];

    return totalPerDiemsArray.reduce((accumulator, perDiem) => (perDiem ? accumulator + 1 : accumulator), 0);
  };

  const getListOfPerDiem = () => {
    if (submissionType === submissionEnum.MONTHLY)
      return perdiems.map((pd) => ({ IsPerDiem: pd.IsPerDiem, Date: dayjs(pd.Date) }));

    return [
      { Date: dayjs(startDateOfPeriod), IsPerDiem: getIsPerDiemByDate(perdiems, dayjs(startDateOfPeriod)) },
      {
        Date: dayjs(startDateOfPeriod).add(1, 'day'),
        IsPerDiem: getIsPerDiemByDate(perdiems, dayjs(startDateOfPeriod).add(1, 'day'))
      },
      {
        Date: dayjs(startDateOfPeriod).add(2, 'day'),
        IsPerDiem: getIsPerDiemByDate(perdiems, dayjs(startDateOfPeriod).add(2, 'day'))
      },
      {
        Date: dayjs(startDateOfPeriod).add(3, 'day'),
        IsPerDiem: getIsPerDiemByDate(perdiems, dayjs(startDateOfPeriod).add(3, 'day'))
      },
      {
        Date: dayjs(startDateOfPeriod).add(4, 'day'),
        IsPerDiem: getIsPerDiemByDate(perdiems, dayjs(startDateOfPeriod).add(4, 'day'))
      },
      {
        Date: dayjs(startDateOfPeriod).add(5, 'day'),
        IsPerDiem: getIsPerDiemByDate(perdiems, dayjs(startDateOfPeriod).add(5, 'day'))
      },
      {
        Date: dayjs(startDateOfPeriod).add(6, 'day'),
        IsPerDiem: getIsPerDiemByDate(perdiems, dayjs(startDateOfPeriod).add(6, 'day'))
      }
    ];
  };

  return (
    <div className={classes.list}>
      {data.map((timesheet) => (
        <div
          role="presentation"
          key={timesheet.Id}
          className={classes.card}
          onClick={() =>
            setOpenTimesheet({
              timesheet,
              startDateOfPeriod
            })
          }
        >
          <div className={classes.doubleText}>
            <p className={classes.location}>{timesheet?.Location}</p>
            <p className={classes.worktype}>{timesheet?.WorkType.Name}</p>
          </div>
          <div className={classes.hoursWrapper}>
            <p className={classes.hours}>
              {submissionType === submissionEnum.MONTHLY &&
                rateType === rateEnum.HOURLY &&
                getHoursAndMinutes(timesheet.TimesheetHoursByDates.reduce((acc, item) => acc + item.TotalHrs, 0))}
              {submissionType === submissionEnum.MONTHLY &&
                rateType === rateEnum.DAILY &&
                `${parseFloat(
                  timesheet.TimesheetHoursByDates.reduce((acc, item) => acc + item.TotalHrs, 0)
                    .toFixed(2)
                    .toString()
                )} d`}
              {submissionType === submissionEnum.WEEKLY &&
                rateType === rateEnum.HOURLY &&
                getHoursAndMinutes(getTotalHours(timesheet, startDateOfPeriod))}
              {submissionType === submissionEnum.WEEKLY &&
                rateType === rateEnum.DAILY &&
                `${parseFloat(getTotalHours(timesheet, startDateOfPeriod))} d`}
            </p>
          </div>
          <div className={classes.arrow}>
            <ArrowForwardIosIcon sx={{ opacity: 0.1 }} />
          </div>
        </div>
      ))}
      {!!getTotalPerDiems(perdiems) && (
        <PerDiemCard
          days={getTotalPerDiems(perdiems)}
          clickHandler={() =>
            setOpenPerDiems({
              data: getListOfPerDiem(),
              isUpdate: true,
              submissionType,
              currentMonth,
              savePDHandler,
              closePDHandler,
              deletePDHandler
            })
          }
        />
      )}
      <div className={classes.btnsContainer}>
        {!isApprover &&
          ![timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(status) && (
            <div role="presentation" onClick={addHandlerMobile}>
              <AddGradientIcon />
            </div>
          )}
        {!getTotalPerDiems(perdiems) && !isApprover && (
          <div
            role="presentation"
            onClick={() =>
              setOpenPerDiems({
                data: getListOfPerDiem(),
                isUpdate: false,
                submissionType,
                currentMonth,
                savePDHandler,
                closePDHandler,
                deletePDHandler
              })
            }
          >
            <BagageIcon />
          </div>
        )}
      </div>
      {openPerDiems && editable && (
        <NotificationModal
          open={openPerDiems}
          onClose={closePDHandler}
          paddingX="5px"
          backgroundColor={colors.PAGE_BACKGROUND}
        >
          <EditPerDiemModal dataSet={openPerDiems} />
        </NotificationModal>
      )}
      {openTimesheet && editable && submissionType === submissionEnum.WEEKLY && (
        <EditTimesheetModal
          open={openTimesheet}
          data={openTimesheet}
          allowedWorkTypes={allowedWorkTypes}
          startDateOfPeriod={startDateOfPeriod}
          saveHandler={(newData) => {
            mobileChangeHandler(newData);
            setOpenTimesheet(null);
          }}
          closeHandler={() => setOpenTimesheet(null)}
          submissionType={submissionType}
          rateType={rateType}
          deleteLineHandler={deleteLineHandler}
          currentMonth={currentMonth}
          editable={editable}
        />
      )}
      {openTimesheet && editable && submissionType === submissionEnum.MONTHLY && (
        <EditMonthlyTimesheetDrawer
          data={openTimesheet}
          closeHandler={() => setOpenTimesheet(null)}
          allowedWorkTypes={allowedWorkTypes}
          saveHandler={(newData) => {
            mobileChangeHandler(newData);
            setOpenTimesheet(null);
          }}
          rateType={rateType}
          deleteLineHandler={deleteLineHandler}
          currentMonth={currentMonth}
        />
      )}
      {openPerDiems && !editable && (
        <BaseSwipeableDrawer closeHandler={() => setOpenPerDiems(null)}>
          <div className={classes.swiper} />
          <div className={classes.container}>
            <div className={classes.header}>
              <p>{t('dashboard.timesheet.perdiem')}</p>
            </div>
            <div className={classes.timesheet}>
              {submissionType === submissionEnum.WEEKLY ? (
                getDatesForWeek(startDateOfPeriod).map((date) => (
                  <PerDiemInfoItem
                    key={date}
                    day={dayjs(date).format('ddd DD')}
                    value={getIsPerDiemByDate(perdiems, date)}
                  />
                ))
              ) : (
                <MonthCalendar
                  data={perdiems}
                  dateProperty="Date"
                  valueProperty="IsPerDiem"
                  monthIndex={currentMonth}
                  disabled
                />
              )}
            </div>
            <div className={`${classes.total} ${classes.withoutBorder}`}>
              <div className={classes.wrapper}>
                <p>{t('dashboard.timesheet.table.total')}</p>
                <p>
                  {getTotalPerDiems(perdiems)} {t('common.general.day')}
                  {getTotalPerDiems(perdiems) !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
          <MainButton size="small" variant="primary" rounded forceWidth="90%" action={() => setOpenPerDiems(null)}>
            {t('common.general.close')}
          </MainButton>
        </BaseSwipeableDrawer>
      )}
      {openTimesheet && !editable && (
        <BaseSwipeableDrawer closeHandler={() => setOpenTimesheet(null)}>
          <div className={classes.swiper} />
          <div className={classes.container}>
            <div className={classes.header}>
              <p>{openTimesheet.timesheet.Location}</p>
              <p>{allowedWorkTypes.find((type) => type.Id === openTimesheet.timesheet.WorkType.Id).Name}</p>
            </div>
            <div className={classes.timesheet}>
              {submissionType === submissionEnum.WEEKLY ? (
                getDatesForWeek(startDateOfPeriod).map((date) => (
                  <TimesheetItem
                    key={date}
                    justForInfo
                    day={dayjs(date).format('ddd DD')}
                    value={getNumberWithDelimeter(getHoursByDate(openTimesheet.timesheet.TimesheetHoursByDates, date))}
                  />
                ))
              ) : (
                <MonthCalendar
                  data={openTimesheet.timesheet.TimesheetHoursByDates}
                  dateProperty="Day"
                  valueProperty="TotalHrs"
                  monthIndex={currentMonth}
                  disabled
                />
              )}
            </div>
            <div className={classes.total}>
              <div className={classes.wrapper}>
                <p>{`${t('dashboard.timesheet.table.total')} ${
                  submissionType === submissionEnum.WEEKLY ? t('common.general.week') : ''
                }`}</p>
                <p>{getTotalHours(openTimesheet.timesheet)}</p>
              </div>
            </div>
            <div className={classes.comment}>
              <div className={classes.contractorName}>
                {isApprover ? (
                  <p>{t('dashboard.timesheet.table.comment')}</p>
                ) : (
                  <>
                    <p>{t('dashboard.timesheet.table.messagefrom')}</p>&nbsp;
                    <p>{`${author.FirstName} ${author.LastName}`}</p>
                  </>
                )}
              </div>
              <div className={classes.message}>{openTimesheet.timesheet.Comment}</div>
            </div>
          </div>
          <div className={classes.fixed}>
            <MainButton size="small" variant="primary" rounded forceWidth="90%" action={() => setOpenTimesheet(null)}>
              {t('common.general.close')}
            </MainButton>
          </div>
        </BaseSwipeableDrawer>
      )}
    </div>
  );
}
